import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function InstallPrompt({ onDismiss }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIOS(ios);
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        onDismiss();
      }
      setDeferredPrompt(null);
    }
  };

  if (isInstalled) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 80 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 80 }}
      transition={{ type: 'spring', damping: 25 }}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '0 16px 32px',
        maxWidth: 480,
        margin: '0 auto',
      }}
    >
      <div style={{
        background: 'rgba(15, 15, 20, 0.95)',
        backdropFilter: 'blur(24px)',
        borderRadius: 24,
        border: '1px solid rgba(255,255,255,0.12)',
        padding: '20px 20px 16px',
        boxShadow: '0 -4px 40px rgba(0,0,0,0.5)',
        position: 'relative',
      }}>
        <button
          onClick={onDismiss}
          style={{
            position: 'absolute', top: 12, right: 16,
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.4)', fontSize: 20,
            cursor: 'pointer', padding: 4,
          }}
        >✕</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <img src="/logo192.png" alt="OiMy" style={{ width: 48, height: 48, borderRadius: 12 }} />
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: 16 }}>Add OiMy to Home Screen</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Get the full app experience</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          {[['🔔', 'Reminders'], ['⚡', 'Faster'], ['📴', 'Works offline']].map(([icon, label]) => (
            <div key={label} style={{
              flex: 1, textAlign: 'center', padding: '8px 4px',
              background: 'rgba(255,255,255,0.06)', borderRadius: 12,
            }}>
              <div style={{ fontSize: 20, marginBottom: 2 }}>{icon}</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{label}</div>
            </div>
          ))}
        </div>

        {deferredPrompt && (
          <button
            onClick={handleInstall}
            style={{
              width: '100%', padding: '14px', borderRadius: 16, border: 'none',
              background: 'white', color: '#0a0a0f', fontSize: 16,
              fontWeight: 700, cursor: 'pointer',
            }}
          >
            Add to Home Screen
          </button>
        )}

        {isIOS && !deferredPrompt && (
          <div style={{
            background: 'rgba(255,255,255,0.06)', borderRadius: 14, padding: '12px 14px',
          }}>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, marginBottom: 10, fontWeight: 600 }}>
              To add to your Home Screen:
            </div>
            {[
              ['1', '📤', 'Tap the Share button in Safari'],
              ['2', '➕', 'Tap "Add to Home Screen"'],
              ['3', '✅', 'Tap "Add" to confirm'],
            ].map(([step, icon, text]) => (
              <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}>
                  {step}
                </div>
                <span style={{ fontSize: 14 }}>{icon}</span>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{text}</span>
              </div>
            ))}
          </div>
        )}

        {!deferredPrompt && !isIOS && (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
            Open this page in Chrome or Safari on your phone for the best experience.
          </div>
        )}

        <button
          onClick={onDismiss}
          style={{
            width: '100%', padding: '10px', marginTop: 10, borderRadius: 12,
            border: 'none', background: 'none',
            color: 'rgba(255,255,255,0.35)', fontSize: 13, cursor: 'pointer',
          }}
        >
          Maybe later
        </button>
      </div>
    </motion.div>
  );
}
